// HierarchyPickerDialog — generic reusable tree picker dialog.
// Used by all four domain move dialogs (risks, controls, events, actions).
// Renders an expandable tree from a flat node list with parent_id references.

import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { X, ChevronRight, ChevronDown } from 'lucide-react'

// --- Types ---

export interface HierarchyNode {
  id: string
  name: string
  parent_id: string | null
}

export interface HierarchyPickerDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (selectedId: string, changeNotes: string) => void
  title: string
  subtitle?: string
  nodeLabel: string
  nodes: HierarchyNode[]
  currentId?: string
  isPending?: boolean
  error?: string | null
}

// --- Internal tree types ---

interface TreeNode extends HierarchyNode {
  children: TreeNode[]
}

// --- Tree node component ---

function PickerNode({
  node,
  depth,
  expandedIds,
  selectedId,
  currentId,
  onToggle,
  onSelect,
}: {
  node: TreeNode
  depth: number
  expandedIds: Set<string>
  selectedId: string | null
  currentId?: string
  onToggle: (id: string) => void
  onSelect: (id: string) => void
}) {
  const isExpanded = expandedIds.has(node.id)
  const isSelected = node.id === selectedId
  const isCurrent = node.id === currentId
  const hasChildren = node.children.length > 0

  return (
    <div>
      <div
        className={cn(
          'flex cursor-pointer items-center gap-1 rounded-sm px-1 py-0.5 text-sm',
          'hover:bg-accent/50',
          isSelected && 'bg-primary/10 font-medium text-primary',
        )}
        style={{ paddingLeft: 4 + depth * 16 }}
        onClick={() => {
          onSelect(node.id)
          if (hasChildren && !isExpanded) {
            onToggle(node.id)
          }
        }}
      >
        {hasChildren ? (
          <button
            className="flex h-4 w-4 items-center justify-center"
            onClick={(e) => {
              e.stopPropagation()
              onToggle(node.id)
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <span className="truncate">{node.name}</span>
        {isCurrent && (
          <span className="ml-1.5 text-xs text-muted-foreground">(current)</span>
        )}
      </div>
      {isExpanded &&
        node.children.map((child) => (
          <PickerNode
            key={child.id}
            node={child}
            depth={depth + 1}
            expandedIds={expandedIds}
            selectedId={selectedId}
            currentId={currentId}
            onToggle={onToggle}
            onSelect={onSelect}
          />
        ))}
    </div>
  )
}

// --- Main dialog ---

export function HierarchyPickerDialog({
  open,
  onClose,
  onConfirm,
  title,
  subtitle,
  nodeLabel,
  nodes,
  currentId,
  isPending,
  error,
}: HierarchyPickerDialogProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [changeNotes, setChangeNotes] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // Reset state when dialog opens/closes
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setSelectedId(null)
      setChangeNotes('')
      setExpandedIds(new Set())
    }
  }

  // Build tree from flat list
  const tree = useMemo<TreeNode[]>(() => {
    const nodeMap = new Map<string, TreeNode>()
    const roots: TreeNode[] = []
    for (const n of nodes) {
      nodeMap.set(n.id, { ...n, children: [] })
    }
    for (const n of nodes) {
      const node = nodeMap.get(n.id)!
      if (n.parent_id && nodeMap.has(n.parent_id)) {
        nodeMap.get(n.parent_id)!.children.push(node)
      } else {
        roots.push(node)
      }
    }
    return roots
  }, [nodes])

  function toggle(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function handleConfirm() {
    if (!selectedId) return
    onConfirm(selectedId, changeNotes)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-base font-semibold">{title}</h2>
            {subtitle && (
              <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-accent">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4">
          {error && (
            <div className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Tree picker */}
          <div>
            <p className="mb-1.5 text-sm font-medium">
              Select destination {nodeLabel}
            </p>
            <div className="max-h-56 overflow-y-auto rounded-md border p-1">
              {nodes.length === 0 ? (
                <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                  No {nodeLabel}s available
                </p>
              ) : (
                tree.map((node) => (
                  <PickerNode
                    key={node.id}
                    node={node}
                    depth={0}
                    expandedIds={expandedIds}
                    selectedId={selectedId}
                    currentId={currentId}
                    onToggle={toggle}
                    onSelect={setSelectedId}
                  />
                ))
              )}
            </div>
          </div>

          {/* Change notes */}
          <div>
            <label className="mb-1.5 block text-sm font-medium" htmlFor="move-notes">
              Reason for move <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id="move-notes"
              value={changeNotes}
              onChange={(e) => setChangeNotes(e.target.value)}
              placeholder="e.g. Reorganising by risk owner..."
              rows={2}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={!selectedId || isPending}
          >
            {isPending ? 'Moving...' : 'Move'}
          </Button>
        </div>
      </div>
    </div>
  )
}
